import { jobLists } from "character";
import FormComponents from "components/forms/FormComponents";
import Link from "components/link/Link";
import NoCharacter from "components/no-character/NoCharacter";
import RelicCheckbox from "components/relic-checkbox/RelicCheckbox";
import { items } from "data-files/items";
import { quests } from "data-files/quests";
import { EorzeaDataType, GameData, InputType, Job, LinkType, RelicData, View } from "enums";
import manager from "managers/app";
import { ViewRelicInfo } from "managers/types/relic-types";
import { Component } from "react";
import { connect } from "react-redux";
import t from "translations/translator";
import { AppState } from "types/app";
import ContentHeader from "views/page-parts/content/content-header/ContentHeader";
import DetailsSection from "views/page-parts/content/details-section/DetailsSection";
import Obtained from "views/page-parts/content/obtained/Obtained";
import OverviewTable from "views/page-parts/content/overview-table/OverviewTable";
import { Requirement } from "views/page-parts/content/relic-requirements/types";
import { ViewWeaponPhantomObscurumProps } from "views/relics/weapons/phantom/obscurum/types";
import { TaskInfo } from "views/type";
import "./WeaponPhantomObscurum.css";

const mapStateToProps = (state:AppState):ViewWeaponPhantomObscurumProps.Redux => {
	return {
		lang: state.userInfo.options.lang,
		gameLang: state.userInfo.options.gameLang,
		userInfo: state.userInfo,
		hideCompletedSteps: state.userInfo.options.hideCompletedSteps,
		hideCompletedTasks: state.userInfo.options.hideCompletedTasks,
		showJob: state.userInfo.options.job,
	};
};

class WeaponPhantomObscurum extends Component<ViewWeaponPhantomObscurumProps.Component> {
	private requirements:Requirement[] = [];
	private importantNPCs:GameData.NPC[] = [];
	private relicInfo:ViewRelicInfo = {
		jobs: jobLists.relic[RelicData.Group.WEAPON_PHANTOM],
		type: RelicData.Type.WEAPON,
		relic: RelicData.Group.WEAPON_PHANTOM,
		part: RelicData.Part.WEAPON_OBSCURUM,
		steps: [RelicData.Step.OBSCURUM_ITEMS, RelicData.Step.CRYSTAL_PASTE, RelicData.Step.WANING_ARCANITE],
		stepPlaceholders: {
			[RelicData.Step.CRYSTAL_PASTE]: { ITEM: GameData.Item.CRYSTAL_PASTE },
			[RelicData.Step.WANING_ARCANITE]: { ITEM: GameData.Item.WANING_ARCANITE },
		},
		next: {
			part: RelicData.Part.WEAPON_ECLIPTICUM,
			view: View.RELIC_WEAPONS_PHANTOM_ECLIPTICUM,
		},
		previous: {
			part: RelicData.Part.WEAPON_UMBRAE,
			view: View.RELIC_WEAPONS_PHANTOM_UMBRAE,
		},
	};

	constructor(props:ViewWeaponPhantomObscurumProps.Component){
		super(props);
		this.requirements = this.getRequirements();
		this.importantNPCs = this.getImportantNPCs();
	}

	render():JSX.Element{
		const character = manager.data.getActiveCharacter();
		if(character === null){ return <NoCharacter />; }

		const completeSteps = manager.content.getCompleteSteps(character.progress, this.relicInfo);

		const itemTaskInfo:TaskInfo[] = [
			// Note: Job doesn't matter in getTaskItem (all jobs need the same item)
			{ task: RelicData.Task.TASK_1, target: manager.relics.getTaskItem(RelicData.Step.OBSCURUM_ITEMS, RelicData.Task.TASK_1) },
			{ task: RelicData.Task.TASK_2, target: manager.relics.getTaskItem(RelicData.Step.OBSCURUM_ITEMS, RelicData.Task.TASK_2) },
			{ task: RelicData.Task.TASK_3, target: manager.relics.getTaskItem(RelicData.Step.OBSCURUM_ITEMS, RelicData.Task.TASK_3) },
			{ task: RelicData.Task.TASK_4, target: manager.relics.getTaskItem(RelicData.Step.OBSCURUM_ITEMS, RelicData.Task.TASK_4) },
		];

		const crystalPasteTaskInfo = [
			{ task: RelicData.Task.TASK_1, target: manager.relics.getTaskItem(RelicData.Step.CRYSTAL_PASTE, RelicData.Task.TASK_1) },
		];

		const arcaniteStepInventory = manager.data.getInventoryStatus({
			item: manager.relics.getTaskItem(RelicData.Step.WANING_ARCANITE, RelicData.Task.TASK_1),
			relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.WANING_ARCANITE, RelicData.Task.TASK_1],
		});

		return (
			<div id="weapon-phantom-obscurum" className="relic">
				<ContentHeader requirements={this.requirements} importantNPCs={this.importantNPCs} relicInfo={this.relicInfo}>
				</ContentHeader>
				<div id="relic-checklist">
					<OverviewTable character={character} completeSteps={completeSteps} relicInfo={this.relicInfo} />
				</div>
				<div id="relic-details">
					<h1>{t.t("Details")}</h1>
					{this.props.hideCompletedSteps && completeSteps.allComplete ? t.t("All Steps Complete") : null }
					<DetailsSection
						title={RelicData.Step.OBSCURUM_ITEMS}
						placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.OBSCURUM_ITEMS]}
						id={RelicData.Step.OBSCURUM_ITEMS}
						stepComplete={completeSteps[RelicData.Step.OBSCURUM_ITEMS]}
					>
						<p>{t.t("Complete")} <Link
							type={LinkType.LODESTONE}
							lodestoneType={EorzeaDataType.BLUEQUEST}
							text={GameData.Quest.TIMEWORN_TECHNIQUES}
							lodestoneID={quests[GameData.Quest.TIMEWORN_TECHNIQUES].lodestoneID}
						/></p>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Obtained")}</th><th>{t.t("Item")}</th><th>{t.t("Done")}</th></tr></thead>
								<tbody>
									{itemTaskInfo.map((thisTask, index) => {
										const target = items[thisTask.target as GameData.Item];
										const inventoryStatus = manager.data.getInventoryStatus({
											item: thisTask.target as GameData.Item,
											relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.OBSCURUM_ITEMS, thisTask.task],
										});

										let qtyToObtain = inventoryStatus.qtyPerJob - inventoryStatus.inventory;
										if(qtyToObtain < 0){ qtyToObtain = 0; }

										/*
											qtyNeeded is only used for recipes, which doesn't apply here but at the time of writeing was required
											REVIEW: Once Obtained component updated remove this
										*/
										return <tr key={`${RelicData.Step.OBSCURUM_ITEMS}-${index}`}>
											<td><Obtained obtained={target.obtained} qtyNeeded={inventoryStatus.qtyPerJob} /></td>
											<td>{t.t(target.name)}</td>
											<td><RelicCheckbox character={character} job={Job.PLD} relicInfo={this.relicInfo} step={RelicData.Step.OBSCURUM_ITEMS} task={thisTask.task} /></td>
										</tr>;
									})}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.OBSCURUM_ITEMS)
											? <tr className="all-complete"><td colSpan={5}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
					<DetailsSection
						title={RelicData.Step.CRYSTAL_PASTE}
						placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.CRYSTAL_PASTE]}
						id={RelicData.Step.CRYSTAL_PASTE}
						stepComplete={completeSteps[RelicData.Step.CRYSTAL_PASTE]}
					>
						<p>{t.t("Complete")} <Link
							type={LinkType.LODESTONE}
							lodestoneType={EorzeaDataType.BLUEQUEST}
							text={GameData.Quest.IN_PURSUIT_OF_PERFECTION}
							lodestoneID={quests[GameData.Quest.IN_PURSUIT_OF_PERFECTION].lodestoneID}
						/></p>
						<div className="table-wrapper">
							<table className="table">
								<thead>
									<tr>
										<th>{t.t("Obtained")}</th>
										<th>{t.t("Item")}</th>
										<th>{t.t("Quantity")}</th>
										<th>{t.t("Total")}</th>
										<th>{t.t("Used")}</th>
										<th>{t.t("Remaining")}</th>
										<th>{t.t("Inventory")}</th>
										<th>{t.t("To Obtain")}</th>
									</tr>
								</thead>
								<tbody>
									{crystalPasteTaskInfo.map((thisTask, index) => {
										const target = items[thisTask.target as GameData.Item];
										const inventoryStatus = manager.data.getInventoryStatus({
											item: thisTask.target as GameData.Item,
											relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.CRYSTAL_PASTE, thisTask.task],
										});

										let qtyToObtain = inventoryStatus.qtyPerJob - inventoryStatus.inventory;
										if(qtyToObtain < 0){ qtyToObtain = 0; }

										/*
											qtyNeeded is only used for recipes, which doesn't apply here but at the time of writeing was required
											REVIEW: Once Obtained component updated remove this
										*/

										return <tr key={`${RelicData.Step.CRYSTAL_PASTE}-${index}`}>
											<td><Obtained obtained={target.obtained} qtyNeeded={inventoryStatus.qtyPerJob} /></td>
											<td>{t.t(target.name)}</td>
											<td>{inventoryStatus.qtyPerJob.toLocaleString()}</td>
											<td>{inventoryStatus.total.toLocaleString()}</td>
											<td>{inventoryStatus.used.toLocaleString()}</td>
											<td>{inventoryStatus.remaining.toLocaleString()}</td>
											<td><FormComponents.TextInput
												label={{ hide: true }}
												input={
													{ type: InputType.NUMBER, value: inventoryStatus.inventory, dataAttributes: { item: thisTask.target }, events: { onChange: manager.content.inventoryUpdated }}
												}
											/></td>
											<td>{inventoryStatus.toObtain.toLocaleString()}</td>
										</tr>;
									})}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.CRYSTAL_PASTE)
											? <tr className="all-complete"><td colSpan={5}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
					<DetailsSection
						title={RelicData.Step.WANING_ARCANITE}
						placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.WANING_ARCANITE]}
						id={RelicData.Step.WANING_ARCANITE}
						stepComplete={completeSteps[RelicData.Step.WANING_ARCANITE]}
					>
						<p>{t.t("Complete")} <Link
							type={LinkType.LODESTONE}
							lodestoneType={EorzeaDataType.BLUEQUEST}
							text={GameData.Quest.A_PHANTOM_REBORN}
							lodestoneID={quests[GameData.Quest.A_PHANTOM_REBORN].lodestoneID}
						/></p>
						<div className="table-wrapper">
							<table className="table">
								<thead>
									<tr>
										<th>{t.t("Obtained")}</th>
										<th>{t.t("Item")}</th>
										<th>{t.t("Quantity / Job")}</th>
										<th>{t.t("Total")}</th>
										<th>{t.t("Used")}</th>
										<th>{t.t("Remaining")}</th>
										<th>{t.t("Inventory")}</th>
										<th>{t.t("To Obtain")}</th>
									</tr>
								</thead>
								<tbody>
									{this.props.hideCompletedTasks && arcaniteStepInventory.toObtain === 0
										? null
										: <tr className="odd-row">
											<td><Obtained obtained={items[GameData.Item.WANING_ARCANITE].obtained} qtyNeeded={arcaniteStepInventory.total} /></td>
											<td>{t.t(items[GameData.Item.WANING_ARCANITE].name)}</td>
											<td>{arcaniteStepInventory.qtyPerJob.toLocaleString()}</td>
											<td>{arcaniteStepInventory.total.toLocaleString()}</td>
											<td>{arcaniteStepInventory.used.toLocaleString()}</td>
											<td>{arcaniteStepInventory.remaining.toLocaleString()}</td>
											<td><FormComponents.TextInput
												label={{ hide: true }}
												input={{
													type: InputType.NUMBER,
													value: arcaniteStepInventory.inventory,
													dataAttributes: { item: GameData.Item.WANING_ARCANITE },
													events: { onChange: manager.content.inventoryUpdated },
												}}
											/></td>
											<td>{arcaniteStepInventory.toObtain.toLocaleString()}</td>
										</tr>
									}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.WANING_ARCANITE)
											? <tr className="all-complete"><td colSpan={8}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
				</div>
			</div>
		);
	}

	componentDidMount():void{
		manager.view.changeComplete();
	}

	private getRequirements():Requirement[]{
		if(!this.relicInfo || this.relicInfo.previous === null){ return []; }

		return [
			{ type: "previous part", part: this.relicInfo.previous.part, partView: this.relicInfo.previous.view },
		];
	}

	private getImportantNPCs():GameData.NPC[]{
		return [];
	}
}
export default connect(mapStateToProps)(WeaponPhantomObscurum);
